/**
 * How much of a scratch card has been rubbed off — pure, so it can be tested
 * without a canvas.
 *
 * The card is split into a coarse grid and every scratch stroke marks the cells it
 * passes through. Coverage is then the share of cells marked. It is deliberately
 * **not** read back from the pixels: an image the realm hosts elsewhere taints the
 * canvas and makes `getImageData` throw, and a per-frame pixel scan is the kind of
 * cost a phone feels. A grid costs a few array writes per move and works whatever
 * the cover is made of.
 */

export interface ScratchGrid {
  cols: number;
  rows: number;
  cells: Uint8Array;
}

export function createScratchGrid(cols = 24, rows = 16): ScratchGrid {
  return { cols, rows, cells: new Uint8Array(cols * rows) };
}

/**
 * Marks every cell within `radius` of the segment (x0,y0)→(x1,y1), in card
 * coordinates of `width` × `height`. Points outside the card are clamped, so a
 * stroke that wanders off the edge still counts where it overlapped.
 */
export function markStroke(
  grid: ScratchGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number,
  width: number,
  height: number,
): void {
  if (width <= 0 || height <= 0) return;
  const cellW = width / grid.cols;
  const cellH = height / grid.rows;

  // Sample the segment finely enough that no cell the brush crosses is skipped.
  const length = Math.hypot(x1 - x0, y1 - y0);
  const steps = Math.max(1, Math.ceil(length / Math.max(1, Math.min(cellW, cellH) / 2)));

  for (let step = 0; step <= steps; step += 1) {
    const t = step / steps;
    const x = x0 + (x1 - x0) * t;
    const y = y0 + (y1 - y0) * t;

    const colFrom = Math.max(0, Math.floor((x - radius) / cellW));
    const colTo = Math.min(grid.cols - 1, Math.floor((x + radius) / cellW));
    const rowFrom = Math.max(0, Math.floor((y - radius) / cellH));
    const rowTo = Math.min(grid.rows - 1, Math.floor((y + radius) / cellH));

    for (let row = rowFrom; row <= rowTo; row += 1) {
      for (let col = colFrom; col <= colTo; col += 1) {
        // A cell counts when the brush reaches its centre, so a graze along an edge
        // does not clear a cell it barely touched.
        const cx = (col + 0.5) * cellW;
        const cy = (row + 0.5) * cellH;
        if (Math.hypot(cx - x, cy - y) <= radius) grid.cells[row * grid.cols + col] = 1;
      }
    }
  }
}

/** The share of the card scratched off, 0…1. */
export function scratchCoverage(grid: ScratchGrid): number {
  let marked = 0;
  for (const cell of grid.cells) marked += cell;
  return marked / grid.cells.length;
}

/** Past this much, the rest of the foil is not worth making anyone rub. */
export const SCRATCH_REVEAL_THRESHOLD = 0.55;
